/**
 * SSEContext - Centralized SSE Connection Manager
 *
 * Provides app-wide SSE connection management that persists across page navigation.
 * Allows components to subscribe to session events without creating new connections.
 *
 * Benefits:
 * - Single SSE connection per session (no reconnection on page nav)
 * - Events persist when navigating between pages
 * - Automatic cleanup when sessions complete
 */

import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import { ParsedEvent } from '../types/session';

export type SSEStatus = 'connecting' | 'open' | 'closed' | 'error';

interface SessionConnection {
  sessionId: string;
  eventSource: EventSource;
  events: ParsedEvent[];
  status: SSEStatus;
  error: string | null;
  reconnectAttempts: number;
}

interface SSEContextValue {
  /**
   * Connect to a session's SSE stream
   */
  connect: (sessionId: string) => void;

  /**
   * Disconnect from a session's SSE stream
   */
  disconnect: (sessionId: string) => void;

  /**
   * Get events for a specific session
   */
  getEvents: (sessionId: string) => ParsedEvent[];

  /**
   * Get SSE connection status for a session
   */
  getStatus: (sessionId: string) => SSEStatus;

  /**
   * Get error message for a session
   */
  getError: (sessionId: string) => string | null;

  /**
   * Subscribe to session updates (for triggering re-renders)
   */
  subscribe: (sessionId: string, callback: () => void) => () => void;
}

const SSEContext = createContext<SSEContextValue | undefined>(undefined);

const MAX_RECONNECT_ATTEMPTS = 3;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const SSEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const connectionsRef = useRef<Map<string, SessionConnection>>(new Map());
  const subscribersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const reconnectTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Force re-render when connections change
  const [, setUpdateTrigger] = useState(0);
  const forceUpdate = useCallback(() => setUpdateTrigger(t => t + 1), []);

  const notifySubscribers = useCallback((sessionId: string) => {
    const subscribers = subscribersRef.current.get(sessionId);
    if (subscribers) {
      subscribers.forEach(callback => callback());
    }
  }, []);

  const closeConnection = useCallback((sessionId: string) => {
    const connection = connectionsRef.current.get(sessionId);
    if (connection) {
      connection.eventSource.close();
      connectionsRef.current.delete(sessionId);
    }

    const timeout = reconnectTimeoutsRef.current.get(sessionId);
    if (timeout) {
      clearTimeout(timeout);
      reconnectTimeoutsRef.current.delete(sessionId);
    }
  }, []);

  const connect = useCallback((sessionId: string) => {
    // Don't reconnect if already connected or connecting
    const existing = connectionsRef.current.get(sessionId);
    if (existing && (existing.status === 'connecting' || existing.status === 'open')) {
      console.log(`[SSEContext] Already connected to session ${sessionId}, status: ${existing.status}`);
      return;
    }

    // Close existing connection if any
    closeConnection(sessionId);

    try {
      // Get auth token from localStorage for SSE connection
      const token = localStorage.getItem('access_token');
      const url = `${API_BASE_URL}/api/sessions/${sessionId}/stream?token=${token}`;
      console.log(`[SSEContext] Connecting to SSE stream: ${url}`);
      const eventSource = new EventSource(url);

      const connection: SessionConnection = {
        sessionId,
        eventSource,
        events: existing?.events || [], // Preserve existing events
        status: 'connecting',
        error: null,
        reconnectAttempts: existing?.reconnectAttempts || 0,
      };

      connectionsRef.current.set(sessionId, connection);
      forceUpdate();

      // Connection opened
      eventSource.onopen = () => {
        console.log(`[SSEContext] SSE connection opened for session ${sessionId}`);
        connection.status = 'open';
        connection.reconnectAttempts = 0;
        forceUpdate();
        notifySubscribers(sessionId);
      };

      // Listen for different event types
      const eventTypes = ['action', 'action_success', 'action_failed', 'final_response', 'initialize_event'];
      eventTypes.forEach(eventType => {
        eventSource.addEventListener(eventType, (e: MessageEvent) => {
          try {
            const event: ParsedEvent = JSON.parse(e.data);
            console.log(`[SSEContext] Received ${eventType} event:`, event);
            // Create new array reference for React to detect change
            connection.events = [...connection.events, event];
            console.log(`[SSEContext] Total events now:`, connection.events.length);
            forceUpdate();
            notifySubscribers(sessionId);
          } catch (err) {
            console.error(`Failed to parse ${eventType} event:`, err);
          }
        });
      });

      // Listen for error events from backend
      eventSource.addEventListener('error', (e: MessageEvent) => {
        try {
          const errorData = JSON.parse(e.data);
          connection.error = errorData.error || 'An error occurred during session execution';
          forceUpdate();
          notifySubscribers(sessionId);
        } catch (err) {
          console.error('Failed to parse error event:', err);
        }
      });

      // Listen for complete event
      eventSource.addEventListener('complete', () => {
        connection.status = 'closed';
        closeConnection(sessionId);
        forceUpdate();
        notifySubscribers(sessionId);
      });

      // Handle connection errors
      eventSource.onerror = () => {
        console.error('SSE connection error for session:', sessionId);
        connection.status = 'error';

        // Attempt reconnection with exponential backoff
        if (connection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, connection.reconnectAttempts), 10000);
          connection.error = `Connection lost. Reconnecting in ${delay / 1000}s...`;
          forceUpdate();
          notifySubscribers(sessionId);

          const timeout = setTimeout(() => {
            connection.reconnectAttempts++;
            connect(sessionId);
          }, delay);

          reconnectTimeoutsRef.current.set(sessionId, timeout);
        } else {
          connection.error = 'Failed to connect after multiple attempts. Please refresh the page.';
          closeConnection(sessionId);
          forceUpdate();
          notifySubscribers(sessionId);
        }
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
      const connection = connectionsRef.current.get(sessionId);
      if (connection) {
        connection.status = 'error';
        connection.error = 'Failed to establish connection';
        forceUpdate();
        notifySubscribers(sessionId);
      }
    }
  }, [closeConnection, forceUpdate, notifySubscribers]);

  const disconnect = useCallback((sessionId: string) => {
    closeConnection(sessionId);
    forceUpdate();
  }, [closeConnection, forceUpdate]);

  const getEvents = useCallback((sessionId: string): ParsedEvent[] => {
    const connection = connectionsRef.current.get(sessionId);
    return connection?.events || [];
  }, []);

  const getStatus = useCallback((sessionId: string): SSEStatus => {
    const connection = connectionsRef.current.get(sessionId);
    return connection?.status || 'closed';
  }, []);

  const getError = useCallback((sessionId: string): string | null => {
    const connection = connectionsRef.current.get(sessionId);
    return connection?.error || null;
  }, []);

  const subscribe = useCallback((sessionId: string, callback: () => void): (() => void) => {
    if (!subscribersRef.current.has(sessionId)) {
      subscribersRef.current.set(sessionId, new Set());
    }
    subscribersRef.current.get(sessionId)!.add(callback);

    // Return unsubscribe function
    return () => {
      const subscribers = subscribersRef.current.get(sessionId);
      if (subscribers) {
        subscribers.delete(callback);
        if (subscribers.size === 0) {
          subscribersRef.current.delete(sessionId);
        }
      }
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      connectionsRef.current.forEach((connection) => {
        connection.eventSource.close();
      });
      reconnectTimeoutsRef.current.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const value: SSEContextValue = {
    connect,
    disconnect,
    getEvents,
    getStatus,
    getError,
    subscribe,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};

/**
 * Hook to use the SSE context
 */
export const useSSEContext = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSEContext must be used within an SSEProvider');
  }
  return context;
};

/**
 * Hook for components to connect to a session and get its events
 */
export const useSessionSSE = (sessionId: string | undefined, enabled: boolean = true) => {
  const { connect, disconnect, getEvents, getStatus, getError, subscribe } = useSSEContext();
  const [, setUpdateTrigger] = useState(0);

  useEffect(() => {
    if (!sessionId || !enabled) {
      return;
    }

    // Connect to the session
    connect(sessionId);

    // Subscribe to updates
    const unsubscribe = subscribe(sessionId, () => {
      setUpdateTrigger(t => t + 1);
    });

    // Cleanup: unsubscribe but DON'T disconnect (keep connection alive for other components)
    return () => {
      unsubscribe();
    };
  }, [sessionId, enabled, connect, subscribe]);

  if (!sessionId) {
    return {
      events: [],
      status: 'closed' as SSEStatus,
      error: null,
    };
  }

  return {
    events: getEvents(sessionId),
    status: getStatus(sessionId),
    error: getError(sessionId),
  };
};
